import axios from "axios";
import type {AssertNoSpecDrift, Schemas, SpecDrift} from "api/schemaAssert";

/**
 * Per-user analytics summary returned by the API's `GET /analytics/summary`
 * (see kitecorp/transferify-api-service#17). Counts are derived from the
 * account's transfers; `lastTransferAt` is null when the account has none.
 *
 * Axios already has `baseURL` set to `REACT_APP_URL`, so the path is relative.
 *
 * <p>STILL LIVE after the Analytics tab moved to embedded Superset dashboards (ui#1490): the profile
 * menu's "Total sent" figure reads `totalBytes` from here. Retiring the endpoint waits until no
 * deployed client calls it (deployed-client-compat rule).
 */
export interface AnalyticsSummary {
	transfers: number;
	filesSent: number;
	totalBytes: number;
	activeTransfers: number;
	expiredTransfers: number;
	totalDownloads: number;
	downloadedTransfers: number;
	/** ISO-8601 timestamp of the most recent transfer, or null if there are none. */
	lastTransferAt: string | null;
}

/**
 * Which dashboard a returned embed target is. The api mints a token that names ONLY the dashboards
 * the caller's plan includes, so `ADVANCED` being absent is the entitlement answer, not an omission.
 */
export type EmbeddedDashboardKind = "BASIC" | "ADVANCED";

/** One embeddable dashboard: the uuid the Superset SDK mounts, and what it is. */
export interface EmbeddedDashboardView {
	uuid: string;
	kind: EmbeddedDashboardKind;
}

/**
 * A guest token and the dashboards it opens (`POST /account/analytics/embed-token`).
 *
 * The token carries a row-level clause pinned to the caller's account and expires in 300 seconds, so
 * it is never stored: {@link embeddedAnalytics.guestTokenFetcher} hands the SDK a function that
 * re-mints on every refresh.
 */
export interface EmbedTokenResponse {
	token: string;
	dashboards: EmbeddedDashboardView[];
}

export type _EmbeddedDashboardViewDrift =
	AssertNoSpecDrift<SpecDrift<EmbeddedDashboardView, Schemas["EmbeddedDashboardView"]>>;
export type _EmbedTokenResponseDrift =
	AssertNoSpecDrift<SpecDrift<EmbedTokenResponse, Schemas["EmbedTokenResponse"]>>;

export class AnalyticsApi {
	/** Fetch the authenticated account's analytics summary. */
	static async getSummary(): Promise<AnalyticsSummary> {
		const {data} = await axios.get<AnalyticsSummary>("/analytics/summary");
		return data;
	}

	/**
	 * Mint a guest token for the account's embedded dashboards (kitecorp/transferify-api-service#1109).
	 *
	 * Takes no body — the api resolves the account from the session — and every call mints a NEW
	 * token, because the previous one is either about to expire or already has.
	 */
	static async mintEmbedToken(): Promise<EmbedTokenResponse> {
		const {data} = await axios.post<EmbedTokenResponse>("/account/analytics/embed-token");
		return data;
	}
}
